/**
 * Group Service for SightTrails Safaris
 * Handles automatic group creation and management when clients book tours
 */

import { supabase } from './supabaseService';
import { tourService } from './tourService';

export interface Group {
  id: string;
  name: string;
  tourId: string;
  tourName: string;
  description: string;
  duration: string;
  startDate: string;
  endDate: string;
  maxCapacity: number;
  currentMembers: number;
  status: 'preparing' | 'active' | 'completed' | 'cancelled';
  guide?: {
    id: string;
    name: string;
    experience: string;
    languages: string[];
    rating: number;
    avatar: string;
  };
  vehicle?: {
    id: string;
    type: string;
    registration: string;
    capacity: number;
    driver: string;
  };
  accommodation?: {
    id: string;
    name: string;
    type: string;
    location: string;
    contact: string;
    phone: string;
    amenities: string[];
  };
  itinerary: Array<{
    day: number;
    title: string;
    description: string;
    activities: string[];
    meals: string[];
    accommodation: string;
    isCompleted: boolean;
    startTime?: string;
    endTime?: string;
  }>;
  gallery: string[];
  includes: string[];
  excludes: string[];
  requirements: string[];
  meetingPoint: string;
  lastActivity: string;
  unreadMessages: number;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMember {
  id: string;
  groupId: string;
  bookingId: string;
  name: string;
  email: string;
  phone: string;
  nationality: string;
  passportNumber?: string;
  dietaryRequirements?: string;
  medicalConditions?: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  joinedAt: string;
  status: 'active' | 'left' | 'removed';
}

export interface ChatMessage {
  id: string;
  groupId: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
  isAdmin: boolean;
  messageType: 'text' | 'image' | 'file' | 'system';
}

class GroupService {
  /**
   * Create a new group automatically when a booking is made
   */
  async createGroupFromBooking(bookingId: string, tourId: string, tourDate: string): Promise<Group> {
    try {
      // 1. Get tour details
      const tour = await tourService.getTourById(tourId);
      if (!tour) {
        throw new Error(`Tour with ID ${tourId} not found`);
      }

      // 2. Calculate end date based on tour duration
      const startDate = new Date(tourDate);
      const durationDays = this.parseDurationToDays(tour.duration);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + durationDays - 1);

      // 3. Generate group name
      const groupName = this.generateGroupName(tour.title, tourDate);

      // 4. Create group data
      // Handle guide_id - convert to UUID if needed, or set to null
      let guideId: string | null = null;
      if (tour.guides && tour.guides.length > 0 && tour.guides[0].id) {
        // Check if guide_id is a valid UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(tour.guides[0].id)) {
          guideId = tour.guides[0].id;
        } else {
          // If not UUID, set to null (will be assigned later)
          console.warn(`Guide ID ${tour.guides[0].id} is not a valid UUID, setting to null`);
          guideId = null;
        }
      }

      const groupData = {
        name: groupName,
        tour_id: String(tourId), // Ensure it's a string (TEXT type in database)
        tour_name: tour.title,
        description: tour.description || '',
        duration: tour.duration || '',
        start_date: tourDate,
        end_date: endDate.toISOString().split('T')[0],
        max_capacity: tour.maxParticipants || 20,
        current_members: 1, // First member (the booker)
        status: 'preparing',
        guide_id: guideId, // UUID or null
        vehicle_id: null, // Will be assigned later by admin
        accommodation_id: null, // Will be assigned later by admin
        itinerary: Array.isArray(tour.itinerary) ? JSON.stringify(tour.itinerary) : (tour.itinerary || '[]'),
        gallery: Array.isArray(tour.images) ? JSON.stringify(tour.images) : (tour.images || '[]'),
        includes: Array.isArray(tour.includes) ? JSON.stringify(tour.includes) : (tour.includes || '[]'),
        excludes: Array.isArray(tour.excludes) ? JSON.stringify(tour.excludes) : (tour.excludes || '[]'),
        requirements: Array.isArray(tour.requirements) ? JSON.stringify(tour.requirements) : (tour.requirements || '[]'),
        meeting_point: (tour as any).meetingPoint || (tour as any).meeting_point || 'To be confirmed',
        last_activity: new Date().toISOString(),
        unread_messages: 0
      };

      // 5. Insert group into database
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert(groupData)
        .select()
        .single();

      if (groupError) {
        throw new Error(`Failed to create group: ${groupError.message}`);
      }

      // 6. Add the booking to the group
      await this.addBookingToGroup(bookingId, group.id);

      // 7. Return the created group with full details
      const createdGroup = await this.getGroupById(group.id);
      if (!createdGroup) {
        // Return the basic group data if we can't get full details
        return this.transformGroupData(group);
      }
      return createdGroup;

    } catch (error) {
      console.error('Error creating group from booking:', error);
      throw error;
    }
  }

  /**
   * Add a booking to an existing group
   */
  async addBookingToGroup(bookingId: string, groupId: string): Promise<void> {
    try {
      // Validate inputs
      if (!bookingId || bookingId.trim() === '') {
        throw new Error('Booking ID is required');
      }
      if (!groupId || groupId.trim() === '') {
        throw new Error('Group ID is required');
      }

      // Validate group ID is UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(groupId)) {
        throw new Error(`Invalid group ID format: ${groupId}`);
      }

      // 1. Check if booking is already in a group
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('id, group_id')
        .eq('booking_id', bookingId)
        .eq('status', 'active')
        .maybeSingle();

      if (existingMember) {
        // Booking is already in a group
        if (existingMember.group_id === groupId) {
           // console.log(`Booking ${bookingId} is already in group ${groupId}`);
          return; // Already in the correct group
        } else {
          // Update to new group
          const { error: updateError } = await supabase
            .from('group_members')
            .update({ group_id: groupId, joined_at: new Date().toISOString() })
            .eq('id', existingMember.id);

          if (updateError) {
            throw new Error(`Failed to update group membership: ${updateError.message}`);
          }

          // Update old group count (decrease)
          await this.updateGroupMemberCount(existingMember.group_id);
          // Update new group count (increase)
          await this.updateGroupMemberCount(groupId);
          return;
        }
      }

      // 2. Get booking details
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .maybeSingle();

      if (bookingError || !booking) {
        throw new Error(`Failed to get booking: ${bookingError?.message || 'Booking not found'}`);
      }

      // 3. Add group member record
      const memberData = {
        group_id: groupId,
        booking_id: bookingId, // TEXT type - matches bookings.id
        name: booking.customer_name || booking.customer_first_name + ' ' + booking.customer_last_name,
        email: booking.customer_email,
        phone: booking.customer_phone,
        nationality: booking.customer_nationality || 'Kenya',
        passport_number: booking.passport_number || null,
        dietary_requirements: booking.dietary_requirements || null,
        medical_conditions: booking.medical_conditions || null,
        emergency_contact: JSON.stringify({
          name: booking.emergency_contact_name || booking.emergency_contact?.name || '',
          phone: booking.emergency_contact_phone || booking.emergency_contact?.phone || '',
          relationship: booking.emergency_contact_relationship || booking.emergency_contact?.relationship || ''
        }),
        status: 'active'
      };

      const { error: memberError } = await supabase
        .from('group_members')
        .insert(memberData);

      if (memberError) {
        // Check if it's a duplicate key error (booking already in group)
        if (memberError.code === '23505') {
           // console.log(`Booking ${bookingId} is already in a group`);
          return;
        }
        throw new Error(`Failed to add member to group: ${memberError.message}`);
      }

      // 4. Update group member count
      await this.updateGroupMemberCount(groupId);

       // console.log(`✅ Successfully added booking ${bookingId} to group ${groupId}`);

    } catch (error: any) {
      console.error('Error adding booking to group:', error);
      throw error;
    }
  }

  /**
   * Get group by ID with full details
   */
  async getGroupById(groupId: string): Promise<Group | null> {
    try {
      // Validate UUID format before querying
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(groupId)) {
        console.error(`Invalid group ID format (not UUID): ${groupId}`);
        return null;
      }

      const { data: group, error } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (error) {
        console.error('Error fetching group:', error);
        return null;
      }

      return this.transformGroupData(group);
    } catch (error) {
      console.error('Error getting group by ID:', error);
      return null;
    }
  }

  /**
   * Get all groups
   */
  async getAllGroups(): Promise<Group[]> {
    try {
      const { data: groups, error } = await supabase
        .from('groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch groups: ${error.message}`);
      }

      return groups.map((group: any) => this.transformGroupData(group));
    } catch (error) {
      console.error('Error getting all groups:', error);
      return [];
    }
  }

  /**
   * Get groups by tour ID
   */
  async getGroupsByTourId(tourId: string): Promise<Group[]> {
    try {
      if (!tourId || tourId.trim() === '') {
        console.warn('Invalid tour ID provided');
        return [];
      }

      const { data: groups, error } = await supabase
        .from('groups')
        .select('*')
        .eq('tour_id', tourId)
        .order('start_date', { ascending: true });

      if (error) {
        // If error is due to type mismatch, try without type constraint
        if (error.message.includes('invalid input syntax') || error.message.includes('type')) {
          console.warn('Tour ID type mismatch, attempting alternative query:', error.message);
          // Try querying all groups and filtering in memory (less efficient but works)
          const { data: allGroups } = await supabase
            .from('groups')
            .select('*')
            .order('start_date', { ascending: true });

          if (allGroups) {
            return allGroups
              .filter((group: any) => String(group.tour_id) === String(tourId))
              .map((group: any) => this.transformGroupData(group));
          }
        }
        throw new Error(`Failed to fetch groups for tour: ${error.message}`);
      }

      return (groups || []).map((group: any) => this.transformGroupData(group));
    } catch (error) {
      console.error('Error getting groups by tour ID:', error);
      return [];
    }
  }

  /**
   * Get group members
   */
  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    try {
      const { data: members, error } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', groupId)
        .eq('status', 'active')
        .order('joined_at', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch group members: ${error.message}`);
      }

      return members.map((member: any) => ({
        ...member,
        emergencyContact: JSON.parse(member.emergency_contact || '{}')
      }));
    } catch (error) {
       // console.log('Error getting group members (table may not exist yet):', error);
      return [];
    }
  }

  /**
   * Update group member count
   */
  private async updateGroupMemberCount(groupId: string): Promise<void> {
    try {
      // Count actual members from database
      const { count, error: countError } = await supabase
        .from('group_members')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', groupId)
        .eq('status', 'active');

      if (countError) {
         // console.log('Could not count group members:', countError.message);
        return; // Don't throw error, just return
      }

      const memberCount = count || 0;

      const { error: updateError } = await supabase
        .from('groups')
        .update({
          current_members: memberCount,
          last_activity: new Date().toISOString()
        })
        .eq('id', groupId);

      if (updateError) {
         // console.log('Could not update member count:', updateError.message);
        // Don't throw error, just log it
      }
    } catch (error) {
       // console.log('Error updating group member count (table may not exist yet):', error);
      // Don't throw error, just log it
    }
  }

  /**
   * Parse duration string to number of days
   */
  private parseDurationToDays(duration: string): number {
    const match = duration.match(/(\d+)\s*(day|days)/i);
    return match ? parseInt(match[1]) : 1;
  }

  /**
   * Generate group name from tour title and date
   */
  private generateGroupName(tourTitle: string, tourDate: string): string {
    const date = new Date(tourDate);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();

    // Extract main location from tour title
    const location = tourTitle.split(' ')[0]; // Take first word as location

    return `${location} Group ${month} ${day}`;
  }

  /**
   * Transform raw group data from database to Group interface
   */
  private transformGroupData(rawGroup: any): Group {
    return {
      id: rawGroup.id,
      name: rawGroup.name,
      tourId: rawGroup.tour_id,
      tourName: rawGroup.tour_name,
      description: rawGroup.description,
      duration: rawGroup.duration,
      startDate: rawGroup.start_date,
      endDate: rawGroup.end_date,
      maxCapacity: rawGroup.max_capacity,
      currentMembers: rawGroup.current_members,
      status: rawGroup.status,
      guide: rawGroup.guide ? {
        id: rawGroup.guide.id,
        name: rawGroup.guide.name,
        experience: rawGroup.guide.experience,
        languages: JSON.parse(rawGroup.guide.languages || '[]'),
        rating: rawGroup.guide.rating,
        avatar: rawGroup.guide.avatar
      } : undefined,
      vehicle: rawGroup.vehicle ? {
        id: rawGroup.vehicle.id,
        type: rawGroup.vehicle.type,
        registration: rawGroup.vehicle.registration,
        capacity: rawGroup.vehicle.capacity,
        driver: rawGroup.vehicle.driver
      } : undefined,
      accommodation: rawGroup.accommodation ? {
        id: rawGroup.accommodation.id,
        name: rawGroup.accommodation.name,
        type: rawGroup.accommodation.type,
        location: rawGroup.accommodation.location,
        contact: rawGroup.accommodation.contact,
        phone: rawGroup.accommodation.phone,
        amenities: JSON.parse(rawGroup.accommodation.amenities || '[]')
      } : undefined,
      itinerary: JSON.parse(rawGroup.itinerary || '[]'),
      gallery: JSON.parse(rawGroup.gallery || '[]'),
      includes: JSON.parse(rawGroup.includes || '[]'),
      excludes: JSON.parse(rawGroup.excludes || '[]'),
      requirements: JSON.parse(rawGroup.requirements || '[]'),
      meetingPoint: rawGroup.meeting_point,
      lastActivity: rawGroup.last_activity,
      unreadMessages: rawGroup.unread_messages,
      createdAt: rawGroup.created_at,
      updatedAt: rawGroup.updated_at
    };
  }
}

export const groupService = new GroupService();
